if (!customElements.get('personalisation-modal')) {
  customElements.define(
    'personalisation-modal',
    class PersonalisationModal extends HTMLElement {
      connectedCallback() {
  
        if (this.parentElement !== document.body) {
          document
            .querySelectorAll(`body > personalisation-modal[data-section-id="${this.dataset.sectionId}"]`)
            .forEach((element) => element.remove());
          document.body.appendChild(this); // re-fires connectedCallback
          return;
        }
        if (this.initialized) return;
        this.initialized = true;

        try {
          this.config = JSON.parse(this.querySelector('[data-personalisation-config]').textContent);
        } catch (error) {
          console.error('personalisation-modal: invalid config', error);
          this.config = null;
          return;
        }
        this.strings = this.config.strings || {};
        this.positions = this.buildPositions();

        this.state = {
          mode: null, // 'none' | 'custom'
          selected: [], // position keys, in selection order
          methods: {}, // key -> 'print' | 'embroidery'
          same: false,
          configs: {}, // key -> per-position config
          sameConfig: this.blankConfig(),
        };

        this.positionsGrid = this.querySelector('[data-positions-grid]');
        this.methodRows = this.querySelector('[data-method-rows]');
        this.configHead = this.querySelector('[data-config-head]');
        this.configCards = this.querySelector('[data-config-cards]');
        this.customArea = this.querySelector('[data-custom-area]');
        this.statusElement = this.querySelector('[data-modal-status]');
        this.saveButton = this.querySelector('[data-modal-save]');
        this.saveLabel = this.querySelector('[data-save-label]');

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        // Component-level so the font list swallows Escape before the
        // document-level handler below closes the whole dialog.
        this.addEventListener('keydown', this.onFontKeydown.bind(this));
        this.onKeydown = this.onKeydown.bind(this);

        // Debug hook: ?branding-debug opens the popup on load,
        // ?branding-debug=custom preselects the customise flow.
        const debugMode = new URLSearchParams(location.search).get('branding-debug');
        if (debugMode !== null) {
          setTimeout(() => {
            this.openModal(document.querySelector('multi-size-form'));
            if (debugMode === 'custom' || debugMode === 'selftest') {
              this.state.mode = 'custom';
              this.positions.slice(0, 4).forEach((position) => this.togglePosition(position.key));
              this.renderAll();
            }
            if (debugMode === 'selftest') {
              try {
                const file = new File(['x'], 'logo.png', { type: 'image/png' });
                this.state.selected.forEach((key) => (this.configFor(key).file = file));
                this.updateFooter();
                const payload = this.buildBrandingPayload();
                const out = document.createElement('pre');
                out.id = 'branding-selftest';
                out.textContent = JSON.stringify({
                  parentIsBody: this.parentElement === document.body,
                  valid: this.customIsValid(),
                  saveDisabled: this.saveButton.hasAttribute('disabled'),
                  extraItems: payload.extraItems,
                  fileItems: payload.fileItems.map((i) => ({ id: i.id, q: i.quantity, props: i.properties, hasFile: Boolean(i.file) })),
                  garmentProps: payload.garmentProps,
                });
                document.body.appendChild(out);
              } catch (error) {
                const out = document.createElement('pre');
                out.id = 'branding-selftest';
                out.textContent = 'SELFTEST ERROR: ' + error.message + '\n' + error.stack;
                document.body.appendChild(out);
              }
            }
          }, 300);
        }
      }

      /* ---------- data ---------- */

      // Merge the print and embroidery lists (index-aligned title/image/fee
      // arrays) into one position record per title.
      buildPositions() {
        const map = new Map();
        const merge = (side, sideKey) => {
          if (!side) return;
          (side.titles || []).forEach((rawTitle, index) => {
            const title = (rawTitle || '').trim();
            if (!title) return;
            const key = title.toLowerCase();
            if (!map.has(key)) map.set(key, { key, title, image: null, print: null, embroidery: null });
            const record = map.get(key);
            record[sideKey] = {
              logoFee: (side.logoFees || [])[index],
              textFee: (side.textFees || [])[index],
            };
            if (!record.image) record.image = (side.images || [])[index] || null;
          });
        };
        merge(this.config.print, 'print');
        merge(this.config.embroidery, 'embroidery');
        return [...map.values()];
      }

      blankConfig() {
        return { type: 'logo', file: null, existing: false, description: '', industry: null, text: '', style: null, font: null };
      }

      positionByKey(key) {
        return this.positions.find((position) => position.key === key);
      }

      configFor(key) {
        if (this.state.same) return this.state.sameConfig;
        if (!this.state.configs[key]) this.state.configs[key] = this.blankConfig();
        return this.state.configs[key];
      }

      findVariant(list, feeValue) {
        const fee = parseFloat(feeValue);
        if (Number.isNaN(fee)) return null;
        // Match on the charged price (pence), not the variant title, so fee
        // variants can be single-variant products whose title checkout hides.
        const pence = Math.round(fee * 100);
        return (list || []).find((variant) => variant.price === pence) || null;
      }

      feeValueFor(position, method, type) {
        const side = position?.[method];
        if (!side) return null;
        return type === 'text' ? side.textFee : side.logoFee;
      }

      methodLabel(method) {
        return method === 'print' ? this.strings.print : this.strings.embroidery;
      }

      /* ---------- open / close ---------- */

      get configurator() {
        return document.querySelector(`order-configurator[data-section="${this.dataset.sectionId}"]`);
      }

      openModal(form) {
        this.form = form;
        this.classList.remove('hidden');
        document.body.classList.add('personalisation-modal-open');
        document.addEventListener('keydown', this.onKeydown);
        // Branding is the final step of the configurator journey.
        this.configurator?.enterVirtualStep?.();
        this.renderAll();
        this.querySelector('[data-modal-close]')?.focus();
      }

      closeModal() {
        this.classList.add('hidden');
        document.body.classList.remove('personalisation-modal-open');
        document.removeEventListener('keydown', this.onKeydown);
        this.configurator?.exitVirtualStep?.();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.closeModal();
      }

      /* ---------- events ---------- */

      onClick(event) {
        // Close on the X, the dimmed backdrop, or any click outside the dialog
        // (event.target is the host itself when the click misses the dialog).
        if (
          event.target === this ||
          event.target.closest('[data-modal-close]') ||
          event.target.closest('[data-modal-overlay]') ||
          !event.target.closest('.personalisation-modal__dialog')
        ) {
          this.closeModal();
          return;
        }

        // Any click landing outside an open font list dismisses it.
        const fontPicker = event.target.closest('[data-font-picker]');
        this.querySelectorAll('[data-font-picker].is-open').forEach((open) => {
          if (open !== fontPicker) this.closeFontList(open);
        });

        const fontOption = event.target.closest('[data-font-option]');
        if (fontOption) {
          this.selectFont(fontPicker, fontOption.dataset.fontOption);
          return;
        }

        if (event.target.closest('[data-font-trigger]')) {
          if (fontPicker.classList.contains('is-open')) this.closeFontList(fontPicker, true);
          else this.openFontList(fontPicker);
          return;
        }

        const clear = event.target.closest('[data-artwork-clear]');
        if (clear) {
          // The button lives inside the upload <label>; preventDefault stops
          // the label from forwarding the click to the file input.
          event.preventDefault();
          this.configFor(clear.dataset.configKey).file = null;
          this.refreshCard(clear.dataset.configKey);
          this.updateFooter();
          return;
        }

        const mode = event.target.closest('[data-mode]');
        if (mode) {
          this.state.mode = mode.dataset.mode;
          this.renderAll();
          return;
        }

        const positionCard = event.target.closest('[data-position-key]');
        if (positionCard && positionCard.closest('[data-positions-grid]')) {
          this.togglePosition(positionCard.dataset.positionKey);
          return;
        }

        const methodButton = event.target.closest('[data-method-choice]');
        if (methodButton && !methodButton.disabled) {
          this.state.methods[methodButton.dataset.methodKey] = methodButton.dataset.methodChoice;
          this.renderMethodRows();
          this.refreshCard(methodButton.dataset.methodKey);
          this.updateFooter();
          return;
        }

        const sameButton = event.target.closest('[data-same-everywhere]');
        if (sameButton) {
          this.state.same = !this.state.same;
          this.renderAll();
          return;
        }

        const tab = event.target.closest('[data-config-tab]');
        if (tab) {
          this.configFor(tab.dataset.configKey).type = tab.dataset.configTab;
          this.refreshCard(tab.dataset.configKey);
          this.updateFooter();
          return;
        }

        const industry = event.target.closest('[data-industry-choice]');
        if (industry) {
          this.configFor(industry.dataset.configKey).industry = industry.dataset.industryChoice;
          this.refreshCard(industry.dataset.configKey);
          this.updateFooter();
          return;
        }

        const style = event.target.closest('[data-style-choice]');
        if (style) {
          this.configFor(style.dataset.configKey).style = style.dataset.styleChoice;
          this.refreshCard(style.dataset.configKey);
          this.updateFooter();
          return;
        }

        if (event.target.closest('[data-modal-save]')) this.onSave();
      }

      onChange(event) {
        const fileInput = event.target.closest('[data-artwork-input]');
        if (fileInput) {
          const config = this.configFor(fileInput.dataset.configKey);
          if (config.existing) return; // upload is disabled while "existing logo" is ticked
          config.file = fileInput.files[0] || null;
          // This user-verified cycle (close + reopen) is the only thing that
          // reliably repaints the dialog after the OS file picker closes, so
          // replicate it automatically across two frames.
          this.reopenForRepaint();
          setTimeout(() => this.logDiagnostic('after file change'), 300);
          return;
        }

        const existing = event.target.closest('[data-existing-input]');
        if (existing) {
          this.configFor(existing.dataset.configKey).existing = existing.checked;
          this.refreshCard(existing.dataset.configKey);
          this.updateFooter();
          return;
        }

      }

      /* ---------- font picker ---------- */

      // A native <select> only styles its own box: <option> font-family is
      // ignored on macOS and iOS, where the list is drawn as an OS menu. A
      // listbox is the only way every row can render in its own face.

      openFontList(picker) {
        this.querySelectorAll('[data-font-picker].is-open').forEach((open) => {
          if (open !== picker) this.closeFontList(open);
        });
        const list = picker.querySelector('.personalisation-modal__font-list');
        picker.classList.add('is-open');
        list.hidden = false;
        picker.querySelector('[data-font-trigger]').setAttribute('aria-expanded', 'true');
        this.activateFontOption(picker, list.querySelector('.is-selected') || list.firstElementChild, false);
        list.focus();
      }

      closeFontList(picker, focusTrigger = false) {
        if (!picker) return;
        const list = picker.querySelector('.personalisation-modal__font-list');
        const trigger = picker.querySelector('[data-font-trigger]');
        picker.classList.remove('is-open');
        if (list) list.hidden = true;
        list?.removeAttribute('aria-activedescendant');
        trigger?.setAttribute('aria-expanded', 'false');
        if (focusTrigger) trigger?.focus();
      }

      // Roving highlight inside the open list. The choice is only committed on
      // Enter or click, so arrowing through does not churn the saved config.
      activateFontOption(picker, option, scroll = true) {
        if (!option) return;
        const list = picker.querySelector('.personalisation-modal__font-list');
        list.querySelectorAll('.is-active').forEach((item) => item.classList.remove('is-active'));
        option.classList.add('is-active');
        list.setAttribute('aria-activedescendant', option.id);
        if (scroll) option.scrollIntoView({ block: 'nearest' });
      }

      selectFont(picker, font) {
        if (!picker || !font) return;
        const config = this.configFor(picker.dataset.configKey);
        config.font = font;
        this.loadFont(font);

        const stack = this.fontStack(font);
        const sample = picker.querySelector('[data-font-trigger-sample]');
        if (sample) {
          sample.style.fontFamily = stack;
          sample.textContent = this.fontSampleText(font, config);
        }
        const name = picker.querySelector('[data-font-trigger-name]');
        if (name) name.textContent = font;
        picker.querySelectorAll('[data-font-option]').forEach((option) => {
          const isSelected = option.dataset.fontOption === font;
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-selected', String(isSelected));
        });

        const preview = picker.closest('.personalisation-modal__config-card')?.querySelector('[data-text-preview]');
        if (preview) preview.style.fontFamily = stack;

        this.closeFontList(picker, true);
        this.updateFooter();
      }

      onFontKeydown(event) {
        const picker = event.target.closest('[data-font-picker]');
        if (!picker) return;
        const list = picker.querySelector('.personalisation-modal__font-list');
        const options = [...picker.querySelectorAll('[data-font-option]')];
        const isOpen = picker.classList.contains('is-open');
        const active = list.querySelector('.is-active') || list.querySelector('.is-selected');
        const move = (delta) => {
          const index = options.indexOf(active);
          const from = index === -1 ? 0 : index;
          this.activateFontOption(picker, options[Math.min(options.length - 1, Math.max(0, from + delta))]);
        };

        switch (event.key) {
          case 'Escape':
            if (!isOpen) return;
            // Beat the modal's document-level Escape handler: the list closes
            // first, the dialog only on a second press.
            event.stopPropagation();
            event.preventDefault();
            this.closeFontList(picker, true);
            return;
          case 'ArrowDown':
          case 'ArrowUp':
            event.preventDefault();
            if (isOpen) move(event.key === 'ArrowDown' ? 1 : -1);
            else this.openFontList(picker);
            return;
          case 'Home':
          case 'End':
            if (!isOpen) return;
            event.preventDefault();
            this.activateFontOption(picker, event.key === 'Home' ? options[0] : options[options.length - 1]);
            return;
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (isOpen) this.selectFont(picker, active?.dataset.fontOption);
            else this.openFontList(picker);
            return;
          case 'Tab':
            if (isOpen) this.closeFontList(picker);
        }
      }

  
      printFonts() {
        const custom = this.config.printFonts || [];
        return custom.length ? custom : window.PersonalisationFonts?.families || [];
      }

     
      fontFor(config) {
        const fonts = this.printFonts();
        return fonts.includes(config.font) ? config.font : fonts[0] || null;
      }

      fontStack(font) {
        return window.PersonalisationFonts?.stack(font) || 'inherit';
      }

      loadFont(font) {
        window.PersonalisationFonts?.load(font);
      }

      // What each row of the font list spells out, rendered in that row's own
      // face. The shopper's own text is the point of the preview; the font's
      // name is the fallback so an empty field still reads as a type specimen.
      fontSampleText(font, config) {
        const typed = (config?.text || '').trim();
        return typed || font;
      }

      // The live specimen above the picker, in the currently chosen font.
      previewText(config) {
        return (config.text || '').trim() || this.strings.enterText;
      }

      // Text fields update state without re-rendering so the input keeps focus.
      onInput(event) {
        const text = event.target.closest('[data-text-input]');
        if (text) {
          const config = this.configFor(text.dataset.configKey);
          config.text = text.value;
          this.refreshFontSamples(text.closest('.personalisation-modal__config-card'), config);
          this.updateFooter();
          return;
        }
        const description = event.target.closest('[data-description-input]');
        if (description) {
          this.configFor(description.dataset.configKey).description = description.value;
          this.updateFooter();
        }
      }

      // Typing repaints the specimen and every row of the font list in place —
      // re-rendering the card here would steal focus from the text field.
      refreshFontSamples(card, config) {
        if (!card) return;
        const preview = card.querySelector('[data-text-preview]');
        if (preview) {
          preview.textContent = this.previewText(config);
          preview.classList.toggle('is-placeholder', !(config.text || '').trim());
        }
        card.querySelectorAll('[data-font-option]').forEach((option) => {
          const sample = option.querySelector('[data-font-sample]');
          if (sample) sample.textContent = this.fontSampleText(option.dataset.fontOption, config);
        });
        const trigger = card.querySelector('[data-font-trigger-sample]');
        if (trigger) trigger.textContent = this.fontSampleText(this.fontFor(config), config);
      }

      togglePosition(key) {
        const index = this.state.selected.indexOf(key);
        if (index === -1) {
          this.state.selected.push(key);
          if (!this.state.methods[key]) {
            const position = this.positionByKey(key);
            this.state.methods[key] = position?.embroidery ? 'embroidery' : 'print';
          }
        } else {
          this.state.selected.splice(index, 1);
        }
        this.renderAll();
      }

      /* ---------- rendering ---------- */

      // For values Liquid did NOT escape: metaobject titles, image URLs and
      // anything the shopper typed.
      escape(value) {
        return String(value ?? '').replace(
          /[&<>"']/g,
          (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
        );
      }

      // Font names become element ids, which aria-activedescendant needs.
      slug(value) {
        return String(value ?? '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }

      // Locale strings arrive already HTML-escaped: Shopify's `t` filter escapes
      // every key that does not end in _html, so an apostrophe reaches the
      // config as &#39; before any JS runs. Passing that through escape() again
      // turns the & into &amp; and the entity shows up as literal text on the
      // page, so translated copy goes out as-is.
      text(key) {
        return String(this.strings[key] ?? '');
      }

      renderAll() {
        this.querySelectorAll('[data-mode]').forEach((button) => {
          button.classList.toggle('is-selected', button.dataset.mode === this.state.mode);
        });
        this.customArea.classList.toggle('hidden', this.state.mode !== 'custom');
        if (this.state.mode === 'custom') {
          this.renderPositions();
          this.renderMethodRows();
          this.renderConfigCards();
        }
        this.updateFooter();
        this.forceRepaint();
      }

      renderPositions() {
        const count = this.querySelector('[data-positions-count]');
        if (count) count.textContent = (this.strings.selectedCount || '[count]').replace('[count]', this.state.selected.length);

        this.positionsGrid.innerHTML = this.positions
          .map((position) => {
            const selected = this.state.selected.includes(position.key);
            const image = position.image
              ? `<img src="${this.escape(position.image)}" alt="" loading="lazy" class="personalisation-modal__position-image">`
              : '<span class="personalisation-modal__position-image personalisation-modal__position-image--empty"></span>';
            return `
              <button type="button" class="personalisation-modal__position${selected ? ' is-selected' : ''}"
                data-position-key="${this.escape(position.key)}" aria-pressed="${selected}">
                ${image}
                <span class="personalisation-modal__position-name">${this.escape(position.title)}</span>
              </button>`;
          })
          .join('');
      }

      // "Other Position" is a free-form placement the workshop has to sign off
      // on, so it is matched by name rather than by a dedicated metaobject
      // field. Rename the position in the metaobject and this keyword is the
      // one thing to keep.
      isOtherPosition(position) {
        return /other/i.test(position?.title || '');
      }

      renderMethodRows() {
        const rows = this.state.selected
          .map((key) => {
            const position = this.positionByKey(key);
            if (!position) return '';
            // Positions live in two independent metaobject lists; one that
            // exists only for print (or only embroidery) offers just that
            // method, so the other button is not rendered at all.
            let method = this.state.methods[key];
            if (!position[method]) {
              method = position.embroidery ? 'embroidery' : 'print';
              this.state.methods[key] = method;
            }
            const button = (choice) => `
              <button type="button" class="personalisation-modal__method-button${method === choice ? ' is-selected' : ''}"
                data-method-choice="${choice}" data-method-key="${this.escape(key)}">
                ${this.methodLabel(choice)}
              </button>`;
            const buttons = [];
            if (position.embroidery) buttons.push(button('embroidery'));
            if (position.print) buttons.push(button('print'));
            // The advisory sits inside the row it applies to, wrapping onto its
            // own line below the name and method buttons.
            const note =
              this.strings.otherPositionNote && this.isOtherPosition(position)
                ? `<span class="personalisation-modal__method-note">${this.text('otherPositionNote')}</span>`
                : '';
            return `
              <div class="personalisation-modal__method-row">
                <span class="personalisation-modal__method-name">${this.escape(position.title)}</span>
                <span class="personalisation-modal__method-toggle">
                  ${buttons.join('')}
                </span>
                ${note}
              </div>`;
          })
          .join('');

        this.methodRows.innerHTML = rows;
      }

      renderConfigCards() {
        const hasSelection = this.state.selected.length > 0;
        this.configHead.classList.toggle('hidden', !hasSelection);
        this.querySelector('[data-same-everywhere]')?.classList.toggle('is-selected', this.state.same);

        if (!hasSelection) {
          this.configCards.innerHTML = '';
          return;
        }

        if (this.state.same) {
          const args = this.cardArgs('__same__');
          this.configCards.innerHTML = this.renderConfigCard(args.key, args.title, args.label, args.config, args.methods);
          return;
        }

        this.configCards.innerHTML = this.state.selected
          .map((key) => {
            const args = this.cardArgs(key);
            if (!args) return '';
            return this.renderConfigCard(args.key, args.title, args.label, args.config, args.methods);
          })
          .join('');
      }

      cardArgs(key) {
        if (key === '__same__') {
          const methods = [...new Set(this.state.selected.map((k) => this.state.methods[k]))];
          return {
            key,
            title: this.strings.sameAll,
            label: methods.map((method) => this.methodLabel(method)).join(' / '),
            config: this.state.sameConfig,
            methods,
          };
        }
        const position = this.positionByKey(key);
        if (!position) return null;
        const method = this.state.methods[key];
        return { key, title: position.title, label: this.methodLabel(method), config: this.configFor(key), methods: [method] };
      }

      // Re-render a single config card in place from state. Cheaper than
      // renderConfigCards (no full-grid rebuild, so no backdrop repaint jank)
      // and guarantees the card always reflects state immediately.
      refreshCard(key) {
        const resolved = this.state.same ? '__same__' : key;
        const card = this.configCards.querySelector(`[data-card-key="${CSS.escape(resolved)}"]`);
        const args = this.cardArgs(resolved);
        if (!card || !args) {
          this.renderConfigCards();
          return;
        }
        card.outerHTML = this.renderConfigCard(args.key, args.title, args.label, args.config, args.methods);
        this.forceRepaint();
      }

      // TEMP DIAGNOSTIC: prints what the browser is actually drawing at the
      // dialog's centre plus the computed visibility of each dialog region.
      logDiagnostic(when) {
        try {
          const dialog = this.querySelector('.personalisation-modal__dialog');
          if (!dialog) return;
          const rect = dialog.getBoundingClientRect();
          const cx = Math.round(rect.left + rect.width / 2);
          const cy = Math.round(rect.top + rect.height / 2);
          const describe = (el) => {
            if (!el) return null;
            const cs = getComputedStyle(el);
            const r = el.getBoundingClientRect();
            return {
              display: cs.display,
              visibility: cs.visibility,
              opacity: cs.opacity,
              rect: [Math.round(r.left), Math.round(r.top), Math.round(r.width), Math.round(r.height)],
            };
          };
          console.warn(
            '[branding-diagnostic]',
            when,
            JSON.stringify({
              stackAtCentre: document
                .elementsFromPoint(cx, cy)
                .slice(0, 8)
                .map((el) => `${el.tagName}.${String(el.className || '').slice(0, 50)}`),
              host: describe(this),
              dialog: describe(dialog),
              header: describe(this.querySelector('.personalisation-modal__header')),
              body: describe(this.querySelector('.personalisation-modal__body')),
              footer: describe(this.querySelector('.personalisation-modal__footer')),
              card: describe(this.configCards.querySelector('.personalisation-modal__config-card')),
              bodyScrollTop: this.querySelector('.personalisation-modal__body')?.scrollTop,
              zoom: window.devicePixelRatio,
              viewport: [window.innerWidth, window.innerHeight],
            })
          );
        } catch (error) {
          console.warn('[branding-diagnostic] failed', error);
        }
      }

      // Programmatic version of the manual fix that always works in the
      // field: fully close and reopen the popup. State is kept in JS, so
      // openModal's renderAll() rebuilds every panel with the fresh data. The
      // hidden state must survive a painted frame (hence double rAF) — a
      // synchronous display toggle never reaches the compositor.
      reopenForRepaint() {
        if (this.classList.contains('hidden') || this.reopenPending) return;
        this.reopenPending = true;
        const form = this.form;
        this.closeModal();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.reopenPending = false;
            this.openModal(form);
          });
        });
      }

      forceRepaint() {
        // retained as a no-op hook; reopenForRepaint() is the effective path
      }

      renderConfigCard(key, title, methodLabel, config, methods) {
        const escapedKey = this.escape(key);
        const tabs = [
          { id: 'logo', label: this.strings.tabLogo },
          ...((this.config.industryLogos || []).length ? [{ id: 'industry', label: this.strings.tabIndustry }] : []),
          { id: 'text', label: this.strings.tabText },
        ]
          .map(
            (tab) => `
              <button type="button" class="personalisation-modal__tab${config.type === tab.id ? ' is-selected' : ''}"
                data-config-tab="${tab.id}" data-config-key="${escapedKey}">
                ${this.escape(tab.label)}
              </button>`
          )
          .join('');

        let body = '';
        if (config.type === 'logo') {
          const fileName = config.file ? this.escape(config.file.name) : '';
          // New upload and existing logo are mutually exclusive: ticking the
          // checkbox disables the upload, and an uploaded file disables the
          // checkbox (the × clears the file to unlock it again).
          const uploadDisabled = config.existing;
          const existingDisabled = Boolean(config.file);
          body = `
            <label class="personalisation-modal__upload${config.file ? ' has-file' : ''}${uploadDisabled ? ' is-disabled' : ''}">
              <input type="file" class="visually-hidden" data-artwork-input data-config-key="${escapedKey}"
                accept="image/*,.pdf,.eps,.ai,.svg" ${uploadDisabled ? 'disabled' : ''}>
              <span class="svg-wrapper personalisation-modal__upload-icon" aria-hidden="true">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <path d="M8 10V2m0 0L5 5m3-3 3 3M2 11v2a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-2"
                    stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <span data-upload-label>${fileName || this.text('uploadArtwork')}</span>
              ${
                config.file
                  ? `<button type="button" class="personalisation-modal__upload-clear" data-artwork-clear
                      data-config-key="${escapedKey}" aria-label="${this.text('uploadArtwork')}">&times;</button>`
                  : ''
              }
            </label>
            <label class="personalisation-modal__existing${existingDisabled ? ' is-disabled' : ''}">
              <input type="checkbox" data-existing-input data-config-key="${escapedKey}" ${config.existing ? 'checked' : ''} ${
                existingDisabled ? 'disabled' : ''
              }>
              <span>
                <strong>${this.text('existingLabel')}</strong>
                <span class="personalisation-modal__existing-desc">${this.text('existingDesc')}</span>
              </span>
            </label>
            ${
              config.existing
                ? `
                  <input type="text" class="personalisation-modal__text-field" data-description-input
                    data-config-key="${escapedKey}" value="${this.escape(config.description)}"
                    placeholder="${this.text('logoDescriptionPlaceholder')}">
                  <p class="personalisation-modal__field-hint caption">${this.text('logoDescriptionHint')}</p>`
                : ''
            }`;
        } else if (config.type === 'industry') {
          body = `
            <div class="personalisation-modal__industry-grid">
              ${(this.config.industryLogos || [])
                .map(
                  (logo) => `
                    <button type="button" class="personalisation-modal__industry${config.industry === logo.name ? ' is-selected' : ''}"
                      data-industry-choice="${this.escape(logo.name)}" data-config-key="${escapedKey}" aria-pressed="${config.industry === logo.name}">
                      <img src="${this.escape(logo.image)}" alt="${this.escape(logo.name)}" loading="lazy">
                    </button>`
                )
                .join('')}
            </div>`;
        } else {
          // Printed text picks a font family; embroidered text keeps the
          // style cards. "Same everywhere" across both methods shows both.
          const styleTitle = this.strings.chooseStyleEmbroidery;
          const printFonts = methods.includes('print') ? this.printFonts() : [];
          const selectedFont = this.fontFor(config);
          // Every row is a specimen of its own face, so every face has to be
          // on the page before the list opens.
          if (printFonts.length) this.loadFont(printFonts);
          const listId = `PersonalisationFontList-${escapedKey}`;
          const labelId = `PersonalisationFontLabel-${escapedKey}`;
          const fontSelect = printFonts.length
            ? `<p class="personalisation-modal__style-title" id="${labelId}">${this.escape(this.strings.chooseFont)}</p>
              <div class="personalisation-modal__font-wrapper" data-font-picker data-config-key="${escapedKey}">
                <button type="button" class="personalisation-modal__font-trigger" data-font-trigger
                  aria-haspopup="listbox" aria-expanded="false" aria-controls="${listId}"
                  aria-labelledby="${labelId} ${listId}-value">
                  <span class="personalisation-modal__font-sample" id="${listId}-value" data-font-trigger-sample
                    style="font-family: ${this.escape(this.fontStack(selectedFont))}">${this.escape(
                      this.fontSampleText(selectedFont, config)
                    )}</span>
                  <span class="personalisation-modal__font-name" data-font-trigger-name>${this.escape(selectedFont)}</span>
                  <span class="svg-wrapper personalisation-modal__font-caret" aria-hidden="true">
                    <svg viewBox="0 0 10 6" width="10" height="6" fill="none"><path d="m1 1 4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </span>
                </button>
                <ul class="personalisation-modal__font-list" id="${listId}" role="listbox" tabindex="-1"
                  aria-labelledby="${labelId}" hidden>
                  ${printFonts
                    .map(
                      (font) => `<li id="${listId}-${this.slug(font)}" role="option" data-font-option="${this.escape(font)}"
                        class="personalisation-modal__font-option${font === selectedFont ? ' is-selected' : ''}"
                        aria-selected="${font === selectedFont}">
                        <span class="personalisation-modal__font-sample" data-font-sample
                          style="font-family: ${this.escape(this.fontStack(font))}">${this.escape(
                            this.fontSampleText(font, config)
                          )}</span>
                        <span class="personalisation-modal__font-name">${this.escape(font)}</span>
                      </li>`
                    )
                    .join('')}
                </ul>
              </div>`
            : '';
          const textPreview = printFonts.length
            ? `<p class="personalisation-modal__text-preview${(config.text || '').trim() ? '' : ' is-placeholder'}"
                data-text-preview aria-hidden="true"
                style="font-family: ${this.escape(this.fontStack(selectedFont))}">${this.escape(
                  this.previewText(config)
                )}</p>`
            : '';
          const styles = (methods.includes('embroidery') ? this.config.textStyles || [] : [])
            .map(
              (style) => `
                <button type="button" class="personalisation-modal__style${config.style === style.label ? ' is-selected' : ''}"
                  data-style-choice="${this.escape(style.label)}" data-config-key="${escapedKey}" aria-pressed="${config.style === style.label}">
                  ${style.image ? `<img src="${this.escape(style.image)}" alt="" loading="lazy">` : ''}
                  <span>${this.escape(style.label)}</span>
                </button>`
            )
            .join('');
          body = `
            <input type="text" class="personalisation-modal__text-field" data-text-input data-config-key="${escapedKey}"
              value="${this.escape(config.text)}" placeholder="${this.text('enterText')}">
            ${textPreview}
            ${fontSelect}
            ${
              styles
                ? `<p class="personalisation-modal__style-title">${this.escape(styleTitle)}</p>
                  <div class="personalisation-modal__style-grid">${styles}</div>`
                : ''
            }`;
        }

        return `
          <div class="personalisation-modal__config-card" data-card-key="${escapedKey}">
            <div class="personalisation-modal__config-head">
              <span class="personalisation-modal__config-title">${this.escape(title)}</span>
              <span class="personalisation-modal__config-method">${this.escape(methodLabel)}</span>
            </div>
            <div class="personalisation-modal__tabs">${tabs}</div>
            ${body}
          </div>`;
      }

      /* ---------- validation / footer ---------- */

      configIsValid(config, methods = []) {
        if (!config) return false;
        if (config.type === 'logo') {
          return config.existing ? config.description.trim().length > 0 : Boolean(config.file);
        }
        if (config.type === 'industry') return Boolean(config.industry);
        if (config.type === 'text') {
          if (!config.text.trim()) return false;
          // Style cards only apply to embroidery; print falls back to the default font.
          if (methods.includes('embroidery') && (this.config.textStyles || []).length && !config.style) return false;
          return true;
        }
        return false;
      }

      customIsValid() {
        if (!this.state.selected.length) return false;
        if (this.state.same) {
          const methods = this.state.selected.map((key) => this.state.methods[key]);
          return this.configIsValid(this.state.sameConfig, methods);
        }
        return this.state.selected.every((key) => this.configIsValid(this.configFor(key), [this.state.methods[key]]));
      }

      updateFooter() {
        let status = '';
        let label = this.strings.save;
        let enabled = false;

        if (this.state.mode === 'none') {
          status = this.strings.plainGarments;
          label = this.strings.addToCart;
          enabled = true;
        } else if (this.state.mode === 'custom') {
          status = (this.strings.placementsCount || '[count]').replace('[count]', this.state.selected.length);
          enabled = this.customIsValid();
        }

        this.statusElement.textContent = status || '';
        this.saveLabel.textContent = label || '';
        this.saveButton.toggleAttribute('disabled', !enabled);
      }

      /* ---------- cart payload ---------- */

      onSave() {
        if (!this.form || this.saveButton.hasAttribute('disabled') || this.saveButton.classList.contains('loading')) return;

        let payload = {};
        if (this.state.mode === 'custom') payload = this.buildBrandingPayload();

        this.saveButton.classList.add('loading');
        this.saveButton.querySelector('.loading__spinner')?.classList.remove('hidden');

        this.form
          .submitItems(payload)
          .then((success) => {
            if (success) this.closeModal();
          })
          .finally(() => {
            this.saveButton.classList.remove('loading');
            this.saveButton.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      buildBrandingPayload() {
        const s = this.strings;
        const feeGroups = new Map(); // variantId + props -> {id, quantity, properties}
        const fileItems = [];
        const garmentProps = {};

        // Hidden token linking this submission's lines: garments carry
        // _branding_group, fee/logo lines carry _branding_fee. The cart hides
        // remove controls on fee lines and cascades their removal when no
        // garment line with the matching group remains (see cart.js).
        const groupToken = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

        // Printing and embroidery are charged per garment, so an application
        // fee line carries one unit per garment this submission adds. The
        // new-logo setup fee below stays at 1 — it is charged once however
        // many garments are branded. cart.js keeps both in step afterwards if
        // the shopper edits quantities in the cart.
        const garmentCount = this.form.selectedItems.reduce((sum, item) => sum + item.quantity, 0);

        const typeLabel = (config) => {
          if (config.type === 'industry') return `${s.typeIndustry}: ${config.industry}`;
          if (config.type === 'text') return s.typeText;
          return config.existing ? s.typeExistingLogo : s.typeNewLogo;
        };

        const addFee = (variant, properties, quantity = 1) => {
          if (!variant) return;
          const groupKey = `${variant.id}|${JSON.stringify(properties)}`;
          if (!feeGroups.has(groupKey)) feeGroups.set(groupKey, { id: variant.id, quantity: 0, properties });
          feeGroups.get(groupKey).quantity += quantity;
        };

        const describe = (config, method) => {
          const parts = [`${s.propMethod}: ${this.methodLabel(method)}`, `${s.propType}: ${typeLabel(config)}`];
          if (config.type === 'text') {
            parts.push(`${s.propText}: ${config.text.trim()}`);
            const font = method === 'print' ? this.fontFor(config) : null;
            if (font) parts.push(`${s.propFont}: ${font}`);
            if (method === 'embroidery' && config.style) parts.push(`${s.propStyle}: ${config.style}`);
          }
          if (config.type === 'logo' && config.existing) parts.push(`${s.propDescription}: ${config.description.trim()}`);
          return parts;
        };

        const applyPosition = (positionLabel, config, method) => {
          const properties = { [s.propPosition]: positionLabel };
          describe(config, method).forEach((part) => {
            const [propKey, ...rest] = part.split(': ');
            properties[propKey] = rest.join(': ');
          });
          properties._branding_fee = groupToken;
          properties._branding_kind = 'fee';
          return properties;
        };

        // positionLabel names the fee line's own position so each charge is
        // identifiable in the cart. setupLabel is kept separate because the
        // one-off setup fee covers every chosen position at once — under
        // "same everywhere" it is deduplicated down to a single line, so
        // naming it after one position would misdescribe it.
        const processOne = (positionLabel, positionRecord, config, method, setupLabel = positionLabel) => {
          // Per-position application fee: Logo Fees variant whose title matches
          // the fee value from the metaobject list for this position + method.
          const feeValue = this.feeValueFor(positionRecord, method, config.type);
          const feeVariant = this.findVariant(this.config.feeVariants, feeValue);
          addFee(feeVariant, applyPosition(positionLabel, config, method), garmentCount);

          // One-off new-logo setup fee: only for a NEW uploaded logo (not
          // existing, not industry). Text and industry logos do not pay it.
          if (config.type === 'logo' && !config.existing) {
            const setupVariant = this.findVariant(this.config.newLogoVariants, this.config.newLogoFees?.[method]);
            if (setupVariant) {
              const properties = {
                [s.propPosition]: setupLabel,
                [s.propMethod]: this.methodLabel(method),
                _branding_fee: groupToken,
                _branding_kind: 'logo',
              };
              fileItems.push({
                id: setupVariant.id,
                quantity: 1,
                properties,
                file: config.file,
                artworkKey: s.propArtwork,
              });
            }
          }
        };

        if (this.state.same) {
          const config = this.state.sameConfig;
          const positionTitles = this.state.selected.map((key) => this.positionByKey(key)?.title).filter(Boolean);
          const sameLabel = `${s.sameAll} (${positionTitles.join(', ')})`;
          this.state.selected.forEach((key) => {
            const position = this.positionByKey(key);
            if (!position) return;
            processOne(position.title, position, config, this.state.methods[key], sameLabel);
          });
          garmentProps[s.brandingKey] = `${s.sameAll}: ${positionTitles.join(', ')} — ${typeLabel(config)}`;
        } else {
          this.state.selected.forEach((key) => {
            const position = this.positionByKey(key);
            if (!position) return;
            const config = this.configFor(key);
            const method = this.state.methods[key];
            processOne(position.title, position, config, method);
            garmentProps[position.title] = `${this.methodLabel(method)} — ${typeLabel(config)}`;
          });
        }

        // A shared new-logo upload is one logo, so same-everywhere only pays
        // one setup fee per method (print and embroidery setups are distinct
        // variants and both apply when the positions mix methods).
        let files = fileItems;
        if (this.state.same && fileItems.length > 1) {
          const seenVariants = new Set();
          files = fileItems.filter((item) => {
            if (seenVariants.has(item.id)) return false;
            seenVariants.add(item.id);
            return true;
          });
        }

        garmentProps._branding_group = groupToken;

        return { extraItems: [...feeGroups.values()], fileItems: files, garmentProps };
      }
    }
  );
}
